export const deepGet = (obj: any, keys: any) => keys.reduce((xs: any, x: any) => xs?.[x] ?? null, obj);

export const delay: any = (function () {
    let timer: any = 0;
    return function (callback: any, ms: any) {
        clearTimeout(timer);
        timer = setTimeout(callback, ms);
    };
})();

export const notNullFilter = (item: any) => item != null

// Интерфейс для правила форматирования для одной подстановки
interface FormatRule {
    condition: {
        field: string;    // Путь для получения значения условия (например, "pointBegin.locationMeter")
        operator: string; // Оператор сравнения: ">", "<", "==", "===" и т.д.
        value: any;       // Значение для сравнения
    };
    trueFormat: string;  // Формат, если условие истинно (например, "+${locationMeter}")
    falseFormat: string; // Формат, если условие ложно (например, "-${locationMeter}")
}

// Функция для разбиения строки пути на токены
function tokenize(path: string): Array<{ type: string; value: string }> {
    const tokens: Array<{ type: string; value: string }> = [];
    // Регулярное выражение ищет:
    // 1. Токены в обратных кавычках (template)
    // 2. Токены в dot-нотации (plain)
    // 3. Токены в квадратных скобках (bracket)
    const regex = /`([^`]+)`|([^[.\]]+)|\[(.*?)\]/g;
    let match;
    while ((match = regex.exec(path)) !== null) {
        if (match[1] !== undefined) {
            tokens.push({ type: 'template', value: match[1] });
        } else if (match[2] !== undefined) {
            tokens.push({ type: 'plain', value: match[2] });
        } else if (match[3] !== undefined) {
            tokens.push({ type: 'bracket', value: match[3] });
        }
    }
    return tokens;
}

// Функция для применения правила форматирования к подстановке
function applyFormatRule(
    rule: FormatRule,
    context: any,
    root: any
): string | undefined {
    // Получаем значение условия по указанному пути (из корневого объекта)
    const condVal = upgradedDeepGet(root, rule.condition.field, valueFormatGlobal, root);
    if (condVal === undefined) return undefined;
    let conditionMet = false;
    switch (rule.condition.operator) {
        case '>':
            conditionMet = condVal > rule.condition.value;
            break;
        case '<':
            conditionMet = condVal < rule.condition.value;
            break;
        case '==':
            conditionMet = condVal == rule.condition.value;
            break;
        case '===':
            conditionMet = condVal === rule.condition.value;
            break;
        // Можно добавить другие операторы по необходимости
        default:
            conditionMet = false;
    }
    // Выбираем формат в зависимости от результата условия
    const fmt = conditionMet ? rule.trueFormat : rule.falseFormat;
    // Выполняем замену подстановок в формате, например, заменяя ${locationMeter} значением из контекста
    return fmt.replace(/\${(.*?)}/g, (_: any, expr: string) => {
        const val = upgradedDeepGet(context, expr.trim(), valueFormatGlobal, root);
        return val === undefined ? '' : val;
    });
}

// Глобальная переменная для передачи valueFormat внутрь рекурсивных вызовов
let valueFormatGlobal: { [key: string]: FormatRule } | undefined;

// Основная функция, аналог _.get, с поддержкой шаблонов, квадратных скобок и кастомного форматирования.
// Параметры:
// - obj: объект для поиска
// - path: строковый путь (например, "pointBegin.`${locationKm}${locationMeter}`")
// - valueFormat: объект правил форматирования (например, для locationMeter)
// - root: корневой объект (если не передан, берется obj)
export function upgradedDeepGet(
    obj: any,
    path: string,
    valueFormat?: { [key: string]: FormatRule },
    root?: any
): any {
    if (root === undefined) {
        root = obj;
    }
    // Передаем valueFormat глобально, чтобы использовать его внутри applyFormatRule
    if (valueFormat !== undefined) {
        valueFormatGlobal = valueFormat;
    }
    const tokens = tokenize(path);
    let current = obj;
    for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];
        let key: any;
        if (
            token.type === 'template' ||
            (token.type === 'plain' && token.value.includes('${'))
        ) {
            // Обрабатываем шаблон: заменяем все вхождения ${...}
            key = token.value.replace(/\${(.*?)}/g, (_: any, expr: string) => {
                expr = expr.trim();
                // Если для данной подстановки задано правило форматирования – применяем его
                if (valueFormat && valueFormat.hasOwnProperty(expr)) {
                    const rule = valueFormat[expr];
                    const formattedPart = applyFormatRule(rule, current, root);
                    return formattedPart === undefined ? '' : formattedPart;
                } else {
                    // Стандартная подстановка
                    const val = upgradedDeepGet(current, expr, valueFormat, root);
                    return val === undefined ? '' : val;
                }
            });
            if (i === tokens.length - 1) {
                return key;
            } else {
                current = current[key];
            }
        } else {
            key = token.value;
            // Обработка массивов: если текущий объект – массив и токен в квадратных скобках
            if (Array.isArray(current) && token.type === 'bracket') {
                if (key === 'last') {
                    current = current[current.length - 1];
                    continue;
                } else if (/^\d+$/.test(key)) {
                    current = current[Number(key)];
                    continue;
                }
            }
            if (i === tokens.length - 1) {
                return current?.[key];
            }
            current = current?.[key];
        }
        if (current === undefined) return undefined;
    }
    return current;
}
